// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Formatadores e Helpers de Exibição
// ═══════════════════════════════════════════════════════════════════
import { T } from "../theme/tokens";

/** Formata valor monetário: 1234.5 → "R$ 1.234,50" · 24077029.98 → "R$ 24.077.029,98" */
export const fmtR = (v) =>
  `R$ ${(+v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

/** Número com separador de milhar, sem símbolo: 24077029.98 → "24.077.029,98" */
export const fmtNum = (v, casas = 2) =>
  (+v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  });

/** Status de vigência de uma tabela de frete */
// `cod` é o identificador ESTÁVEL do estado; `label` é texto de tela e muda.
// Filtro e comparação usam cod — "Vence em 12d" é interpolado e nenhuma
// comparação por igualdade contra ele jamais acerta.
//
// Sem data de fim = vigência aberta, tratada como vigente: é o caso de
// contrato sem prazo definido, não de tabela vencida.
export function vigStatus(fim) {
  // Vazio primeiro: new Date(null) devolve 01/01/1970, uma data VÁLIDA e no
  // passado — sem esta guarda, tabela sem data de fim aparecia como expirada.
  if (!fim) return { cod:"vigente", label:"Vigente", color:T.green };
  const now = new Date(); now.setHours(0, 0, 0, 0);
  // ⚠ new Date("2026-08-11") é lido como UTC: no fuso -03:00 vira dia 10 às
  // 21h e o setHours(0,0,0,0) derruba para o dia 10. Toda tabela expirava um
  // dia ANTES do combinado, e no último dia de validade já sumia dos filtros
  // que exigem vigência (Conferência, AuditoriaCTe). Fixar meio-dia local
  // resolve — mesma solução usada em negocio.js.
  const bruto = typeof fim === "string" && /^\d{4}-\d{2}-\d{2}$/.test(fim)
    ? fim + "T12:00:00" : fim;
  const f   = new Date(bruto); f.setHours(0, 0, 0, 0);
  if (isNaN(f)) return { cod:"vigente", label:"Vigente", color:T.green };
  const d   = Math.ceil((f - now) / 864e5);
  if (f < now) return { cod:"expirada", label:"Expirada",      color:T.red   };
  if (d <= 30) return { cod:"a_vencer", label:`Vence em ${d}d`, color:T.amber };
  return             { cod:"vigente",  label:"Vigente",         color:T.green };
}

/** SLA de uma ocorrência (urgente / sem_trat / warn / ok) */
export function slaInfo(card) {
  if (card.status === "finalizado") return null;
  const dt = card.dataAberturaISO ? new Date(card.dataAberturaISO) : null;
  if (!dt) return null;
  const h = (Date.now() - dt.getTime()) / 3600000;
  const d = h / 24;
  if (d > 7)    return { tipo: "urgente",  label: `${Math.floor(d)}d — URGENTE`,       color: T.red    };
  if (h > 24 && card.status === "nao_iniciado")
                return { tipo: "sem_trat", label: `${Math.floor(h)}h sem tratativa`,   color: "#f97316" };
  if (d > 4.9)  return { tipo: "warn",     label: `${Math.floor(d)}d de 7`,            color: T.amber  };
  return              { tipo: "ok",        label: `${Math.floor(h)}h`,                 color: T.green  };
}

/**
 * "DD/MM/AAAA" → "AAAA-MM-DD". Devolve "" quando não é data no formato BR.
 *
 * Estava escrita igual em Comparativo e NotasFiscais (#139). Helper
 * duplicado por tela é o que o AGENTS.md §6 proíbe: a regra se corrige num
 * lugar e fica velha no outro.
 */
export function brParaISO(br) {
  if (!br) return "";
  const p = br.split("/");
  if (p.length !== 3) return "";
  return p[2] + "-" + p[1] + "-" + p[0];
}

/** "AAAA-MM-DD" → "DD/MM/AAAA". Devolve "" quando não é data ISO. */
export function isoParaBR(iso) {
  return /^\d{4}-\d{2}-\d{2}/.test(iso || "") ? iso.slice(0, 10).split("-").reverse().join("/") : "";
}

/**
 * Quando um alerta aconteceu (#159): "28/09/2026 14:32 · há 3 dias".
 * Data pura ("2026-09-28") sai sem hora e é lida no fuso LOCAL — new Date()
 * a leria como UTC, e 01/09 viraria 31/08 às 21h no Brasil. Data futura
 * (tabela que ainda vai vencer) sai como "em N dias". null se não for data.
 */
export function fmtQuando(em, agora = new Date()) {
  if (!em) return null;
  const soData = /^\d{4}-\d{2}-\d{2}$/.test(em);
  const d = new Date(soData ? em + "T00:00" : em);
  if (isNaN(d)) return null;
  const txt = soData ? d.toLocaleDateString("pt-BR")
    : d.toLocaleString("pt-BR", { dateStyle:"short", timeStyle:"short" }).replace(",", "");
  const meiaNoite = x => { const c = new Date(x); c.setHours(0, 0, 0, 0); return c; };
  const dias = Math.round((meiaNoite(agora) - meiaNoite(d)) / 86400000);
  const rel = dias === 0 ? "hoje" : dias === 1 ? "ontem"
    : dias > 0 ? `há ${dias} dias` : `em ${-dias} dia(s)`;
  return `${txt} · ${rel}`;
}
